import React from 'react';
import PropType from 'prop-types';
import { Field } from 'redux-form';
import { withStyles } from '@material-ui/styles';
import { Grid } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from 'components/ExpansionPanel/ExpansionPanelSummary';
import RenderFormInput from 'components/forms/RenderFormInput';
import { requiredValidator, handleBlur } from 'utils/validators';
import TagsInput from 'react-tagsinput';
import RenderTag from 'components/forms/RenderTag';
import { CONTACT_CUSTOM_FIELDS } from 'constants/contact';


const advancedFields = [
  {
    id: 'industries',
    name: 'industries',
    label: 'Industries',
    component: propsCus => (
      <TagsInput
        addOnBlur
        addKeys={['Enter', 'Tab']}
        focusedClassName=""
        inputProps={{
          className: 'form-control',
          placeholder: 'Type and press enter',
        }}
        renderTag={RenderTag}
        value={propsCus.input.value || []}
        onChange={propsCus.input.onChange}
        onBlur={() => propsCus.input.onBlur(propsCus.input.value)}
        disabled={propsCus.disabled}
      />
    ),
  },
  {
    id: 'outlet_unique_monthly_visitors',
    name: 'outlet.unique_monthly_visitors',
    label: 'Unique Monthly Visitors',
    component: RenderFormInput,
  },
  {
    id: 'outlet_audience',
    name: 'outlet.audience',
    label: 'Audience',
    component: RenderFormInput,
  },
  {
    id: 'outlet_country',
    name: 'outlet.country',
    label: 'Country',
    component: RenderFormInput,
  },
  {
    id: 'outlet_dma',
    name: 'outlet.dma',
    label: 'DMA',
    component: RenderFormInput,
  },
  {
    id: 'outlet_alexa_score',
    name: 'outlet.alexa_score',
    label: 'Alexa Score',
    component: RenderFormInput,
  },
];


const styles = theme => ({
  advancedFieldsPanel: {
    background: 'transparent',
    border: `1px solid ${theme.palette.secondary['300']}`,
    borderRadius: '4px',
    marginTop: '1rem',
    boxShadow: 'none',
    '&:before': {
      content: 'none !important',
    },
  },
  advancedFieldsPanelTitle: {
    fontWeight: 600,
    fontSize: '14px',
    letterSpacing: '-0.2px',
  },
});


const ContactFormFields = ({
  classes,
  viewMode,
  disableEmail,
  showCustomField,
}) => (
  <React.Fragment>
    <Grid container spacing={2}>
      <Grid item sm={6}>
        {/* eslint-disable-next-line jsx-a11y/label-has-for */}
        <label htmlFor="first_name" className="mt-4 required">First Name</label>
        <Field
          id="first_name"
          name="first_name"
          component={RenderFormInput}
          validate={[requiredValidator]}
          onBlur={handleBlur}
          disabled={viewMode}
        />
      </Grid>
      <Grid item sm={6}>
        {/* eslint-disable-next-line jsx-a11y/label-has-for */}
        <label htmlFor="last_name" className="mt-4">Last Name</label>
        <Field
          id="last_name"
          name="last_name"
          component={RenderFormInput}
          onBlur={handleBlur}
          disabled={viewMode}
        />
      </Grid>
    </Grid>
    {/* eslint-disable-next-line jsx-a11y/label-has-for */}
    <label htmlFor="email" className="mt-4 required">Email</label>
    <Field
      id="email"
      name="email"
      component={RenderFormInput}
      validate={[requiredValidator]}
      onBlur={handleBlur}
      disabled={disableEmail}
    />
    <hr className="mt-4 mb-4" />
    {/* eslint-disable-next-line jsx-a11y/label-has-for */}
    <label htmlFor="title" className="mt-0">Title</label>
    <Field
      id="title"
      name="title"
      component={RenderFormInput}
      onBlur={handleBlur}
      disabled={viewMode}
    />
    {/* eslint-disable-next-line jsx-a11y/label-has-for */}
    <label htmlFor="outlet_name" className="mt-4 required">Outlet Name</label>
    <Field
      id="outlet_name"
      name="outlet.name"
      component={RenderFormInput}
      validate={[requiredValidator]}
      onBlur={handleBlur}
      disabled={viewMode}
    />
    {/* eslint-disable-next-line jsx-a11y/label-has-for */}
    <label htmlFor="subject" className="mt-4">Contact Subjects (recommended)</label>
    <Field
      id="subject"
      name="subject"
      component={RenderFormInput}
      onBlur={handleBlur}
      disabled={viewMode}
    />
    {/* eslint-disable-next-line jsx-a11y/label-has-for */}
    <label htmlFor="outlet_website" className="mt-4">Outlet Website (recommended) ex. https://www.prvolt.com</label>
    <Field
      id="outlet_website"
      name="outlet.website"
      component={RenderFormInput}
      onBlur={handleBlur}
      disabled={viewMode}
    />
    {/* eslint-disable-next-line jsx-a11y/label-has-for */}
    <label htmlFor="outlet_type" className="mt-4">Outlet Type (recommended)</label>
    <Field
      id="outlet_type"
      name="outlet.outlet_type"
      component={RenderFormInput}
      onBlur={handleBlur}
      disabled={viewMode}
    />
    <ExpansionPanel className={classes.advancedFieldsPanel}>
      <ExpansionPanelSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="advanced-fields"
        id="advanced-fields"
        className={classes.advancedFieldsPanelTitle}
      >
        Advanced Fields
      </ExpansionPanelSummary>
      <ExpansionPanelDetails className="flex-column">
        {advancedFields.map(field => (
          <>
            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
            <label htmlFor={field.id} className="mt-4">{field.label}</label>
            <Field
              id={field.id}
              name={field.name}
              component={field.component}
              onBlur={handleBlur}
              disabled={viewMode}
            />
          </>
        ))}
        {showCustomField && CONTACT_CUSTOM_FIELDS.map(field => (
          <>
            {/* eslint-disable-next-line jsx-a11y/label-has-for */}
            <label htmlFor={`cf-${field}`} className="mt-4">{field}</label>
            <Field
              id={`cf-${field}`}
              name={`custom_field.${field}`}
              component={RenderFormInput}
              onBlur={handleBlur}
              disabled={viewMode}
            />
          </>
        ))}
      </ExpansionPanelDetails>
    </ExpansionPanel>
  </React.Fragment>
);

ContactFormFields.propTypes = {
  classes: PropType.shape({}).isRequired,
  viewMode: PropType.bool,
  disableEmail: PropType.bool,
  showCustomField: PropType.bool,
};

ContactFormFields.defaultProps = {
  viewMode: false,
  disableEmail: false,
  showCustomField: true,
};

export default withStyles(styles)(ContactFormFields);
